/**
 * Büro-Einstellungen: Firmendaten, Logo-Upload, Briefkopf-Konfiguration, DSGVO-Dokumente
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Building2, Upload, Trash2, Save, Phone, Globe, Mail, MapPin,
  FileSignature, Loader2, CheckCircle, Image as ImageIcon,
  Shield, FileDown, Download, CheckCircle2, AlertTriangle, ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const API = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

function getAuthHeaders() {
  const token = localStorage.getItem('olympus_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
}

export default function BueroEinstellungenPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [dsgvoStatus, setDsgvoStatus] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    firmenname: '', firmenname_zusatz: '', inhaber: '',
    strasse: '', plz: '', ort: '',
    telefon: '', fax: '', website: '', email: '',
    unterzeichner: '', unterzeichner_titel: '',
  });

  const loadData = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/buero/einstellungen`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setForm({
          firmenname: data.firmenname || '',
          firmenname_zusatz: data.firmenname_zusatz || '',
          inhaber: data.inhaber || '',
          strasse: data.strasse || '',
          plz: data.plz || '',
          ort: data.ort || '',
          telefon: data.telefon || '',
          fax: data.fax || '',
          website: data.website || '',
          email: data.email || '',
          unterzeichner: data.unterzeichner || '',
          unterzeichner_titel: data.unterzeichner_titel || '',
        });
        if (data.logo_base64) {
          setLogoPreview(`data:${data.logo_mimetype || 'image/png'};base64,${data.logo_base64}`);
        }
      }
    } catch (e) {
      console.error(e);
    }
    // DSGVO Status laden
    try {
      const dRes = await fetch(`${API}/api/dsgvo/status`, { headers: getAuthHeaders() });
      if (dRes.ok) setDsgvoStatus(await dRes.json());
    } catch (e) { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/buero/einstellungen`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(form),
      });
      if (res.ok) toast.success('Einstellungen gespeichert');
      else toast.error('Fehler beim Speichern');
    } catch (e) {
      toast.error('Fehler beim Speichern');
    }
    setSaving(false);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo darf max. 2 MB groß sein');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch(`${API}/api/buero/logo`, { method: 'POST', body: formData, headers: getAuthHeaders() });
      if (res.ok) {
        const reader = new FileReader();
        reader.onload = (ev) => setLogoPreview(ev.target.result);
        reader.readAsDataURL(file);
        toast.success('Logo hochgeladen');
      } else {
        const err = await res.json();
        toast.error(err.fehler || 'Upload fehlgeschlagen');
      }
    } catch (e) {
      toast.error('Upload fehlgeschlagen');
    }
  };

  const handleLogoDelete = async () => {
    try {
      await fetch(`${API}/api/buero/logo`, { method: 'DELETE', headers: getAuthHeaders() });
      setLogoPreview(null);
      toast.success('Logo entfernt');
    } catch (e) {
      toast.error('Fehler');
    }
  };

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleDownloadDSGVO = async (endpoint, filename) => {
    setDownloading(endpoint);
    try {
      const res = await fetch(`${API}${endpoint}`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Download fehlgeschlagen');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${filename} heruntergeladen`);
    } catch (e) {
      toast.error('Download fehlgeschlagen');
    }
    setDownloading(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="buero-einstellungen-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Building2 className="w-6 h-6 text-slate-600" />
          <h1 className="text-xl font-semibold text-slate-800">Büro-Einstellungen</h1>
        </div>
        <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="btn-save-buero">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Speichern
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Logo */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ImageIcon className="w-4 h-4" /> Firmenlogo
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 flex flex-col items-center justify-center min-h-[140px] bg-slate-50">
              {logoPreview ? (
                <img
                  src={logoPreview}
                  alt="Firmenlogo"
                  className="max-h-24 max-w-full object-contain"
                  data-testid="logo-preview"
                />
              ) : (
                <div className="text-center text-slate-400">
                  <ImageIcon className="w-10 h-10 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">Kein Logo hochgeladen</p>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/svg+xml"
                className="hidden"
                onChange={handleLogoUpload}
              />
              <Button
                variant="outline"
                size="sm"
                className="flex-1 gap-1"
                onClick={() => fileInputRef.current?.click()}
                data-testid="btn-upload-logo"
              >
                <Upload className="w-3 h-3" /> Hochladen
              </Button>
              {logoPreview && (
                <Button variant="outline" size="sm" className="text-red-500" onClick={handleLogoDelete}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              )}
            </div>
            <p className="text-xs text-slate-400">PNG, JPG oder SVG, max. 2 MB</p>
          </CardContent>
        </Card>

        {/* Firmendaten */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="w-4 h-4" /> Firmendaten
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Firmenname</Label>
                <Input value={form.firmenname} onChange={e => updateField('firmenname', e.target.value)}
                  placeholder="ProCura Bayern" data-testid="input-firmenname" />
              </div>
              <div>
                <Label>Zusatz</Label>
                <Input value={form.firmenname_zusatz} onChange={e => updateField('firmenname_zusatz', e.target.value)}
                  placeholder="Berufsbetreuung Daniel Karg" data-testid="input-zusatz" />
              </div>
              <div>
                <Label>Inhaber</Label>
                <Input value={form.inhaber} onChange={e => updateField('inhaber', e.target.value)}
                  placeholder="Daniel Karg" />
              </div>
              <div>
                <Label className="flex items-center gap-1"><Mail className="w-3 h-3" /> E-Mail</Label>
                <Input value={form.email} onChange={e => updateField('email', e.target.value)}
                  placeholder="kontakt@procura.bayern" />
              </div>
              <div className="md:col-span-2">
                <Label className="flex items-center gap-1"><MapPin className="w-3 h-3" /> Adresse</Label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  <Input value={form.strasse} onChange={e => updateField('strasse', e.target.value)}
                    placeholder="Edison Str. 1" className="col-span-3 md:col-span-1" />
                  <Input value={form.plz} onChange={e => updateField('plz', e.target.value)}
                    placeholder="86199" />
                  <Input value={form.ort} onChange={e => updateField('ort', e.target.value)}
                    placeholder="Augsburg" />
                </div>
              </div>
              <div>
                <Label className="flex items-center gap-1"><Phone className="w-3 h-3" /> Telefon</Label>
                <Input value={form.telefon} onChange={e => updateField('telefon', e.target.value)}
                  placeholder="0821 806811-40" />
              </div>
              <div>
                <Label className="flex items-center gap-1"><Phone className="w-3 h-3" /> Fax</Label>
                <Input value={form.fax} onChange={e => updateField('fax', e.target.value)}
                  placeholder="+49 261 2016182592" />
              </div>
              <div className="md:col-span-2">
                <Label className="flex items-center gap-1"><Globe className="w-3 h-3" /> Website</Label>
                <Input value={form.website} onChange={e => updateField('website', e.target.value)}
                  placeholder="www.procura.bayern" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Unterschrift */}
        <Card className="lg:col-span-3">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <FileSignature className="w-4 h-4" /> Briefunterschrift
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Unterzeichner</Label>
                <Input value={form.unterzeichner} onChange={e => updateField('unterzeichner', e.target.value)}
                  placeholder="Daniel Karg" data-testid="input-unterzeichner" />
                <p className="text-xs text-slate-400 mt-1">Die digitale Unterschrift variiert bei jedem Brief leicht</p>
              </div>
              <div>
                <Label>Titel / Funktion</Label>
                <Input value={form.unterzeichner_titel} onChange={e => updateField('unterzeichner_titel', e.target.value)}
                  placeholder="Rechtlicher Betreuer" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ==================== DSGVO DOKUMENTE ==================== */}
      <div className="mt-8" data-testid="dsgvo-section">
        <div className="flex items-center gap-3 mb-4">
          <Shield className="w-6 h-6 text-emerald-600" />
          <h2 className="text-lg font-semibold text-slate-800">DSGVO-Dokumentation</h2>
        </div>

        {/* Technischer Status */}
        {dsgvoStatus && (
          <Card className="mb-4 border-emerald-200 bg-emerald-50/40">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2 text-emerald-800">
                <CheckCircle2 className="w-4 h-4" /> Technische Maßnahmen (Art. 32 DSGVO)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {Object.entries(dsgvoStatus.technisch || {}).map(([key, val]) => (
                  <div key={key} className="flex items-center gap-1.5 text-xs">
                    {val
                      ? <CheckCircle2 className="w-3 h-3 text-emerald-600 shrink-0" />
                      : <AlertTriangle className="w-3 h-3 text-amber-500 shrink-0" />}
                    <span className="text-slate-700 truncate">{key.replace(/_/g, ' ')}</span>
                  </div>
                ))}
              </div>
              {dsgvoStatus.offen && Object.keys(dsgvoStatus.offen).length > 0 && (
                <div className="mt-3 pt-3 border-t border-emerald-200">
                  <p className="text-xs font-medium text-amber-700 mb-1 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Offene Punkte:
                  </p>
                  {Object.entries(dsgvoStatus.offen).map(([key, val]) => (
                    <div key={key} className="text-xs text-slate-600 ml-4 mb-0.5">
                      {key.replace(/_/g, ' ')}: {val}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Dokument-Downloads */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { endpoint: '/api/dsgvo/dokumente/vvt', filename: 'VVT_Olympus.pdf', title: 'VVT', desc: 'Verzeichnis der Verarbeitungstätigkeiten (Art. 30)', article: 'Art. 30 DSGVO' },
            { endpoint: '/api/dsgvo/dokumente/dsfa', filename: 'DSFA_Olympus.pdf', title: 'DSFA', desc: 'Datenschutz-Folgenabschätzung', article: 'Art. 35 DSGVO' },
            { endpoint: '/api/dsgvo/dokumente/toms', filename: 'TOMs_Olympus.pdf', title: 'TOMs', desc: 'Technische & Organisatorische Maßnahmen', article: 'Art. 32 DSGVO' },
            { endpoint: '/api/dsgvo/dokumente/datenschutzhinweise', filename: 'Datenschutzhinweise_Olympus.pdf', title: 'Datenschutzhinweise', desc: 'Information für Betreute', article: 'Art. 13/14 DSGVO' },
            { endpoint: '/api/dsgvo/dokumente/backup-strategie', filename: 'Backup_Strategie_Olympus.pdf', title: 'Backup-Strategie', desc: 'Datensicherungs- und Wiederherstellungsplan', article: 'Art. 32 DSGVO' },
          ].map((doc) => (
            <Card key={doc.endpoint} className="hover:shadow-md transition-shadow" data-testid={`dsgvo-card-${doc.title.toLowerCase().replace(/[^a-z]/g, '')}`}>
              <CardContent className="pt-5 pb-4 px-5 flex flex-col h-full">
                <div className="flex items-start gap-3 mb-3 flex-1">
                  <div className="p-2 rounded-md bg-slate-100 shrink-0">
                    <FileDown className="w-4 h-4 text-slate-600" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-slate-800">{doc.title}</h3>
                    <p className="text-xs text-slate-500 mt-0.5">{doc.desc}</p>
                    <span className="inline-block text-[10px] mt-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 font-mono">{doc.article}</span>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full gap-2 mt-auto"
                  disabled={downloading === doc.endpoint}
                  onClick={() => handleDownloadDSGVO(doc.endpoint, doc.filename)}
                  data-testid={`btn-download-${doc.title.toLowerCase().replace(/[^a-z]/g, '')}`}
                >
                  {downloading === doc.endpoint
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <Download className="w-3.5 h-3.5" />}
                  PDF herunterladen
                </Button>
              </CardContent>
            </Card>
          ))}

          {/* AVV Hetzner Hinweis */}
          <Card className="border-dashed border-amber-300 bg-amber-50/30" data-testid="dsgvo-card-avv">
            <CardContent className="pt-5 pb-4 px-5 flex flex-col h-full">
              <div className="flex items-start gap-3 mb-3 flex-1">
                <div className="p-2 rounded-md bg-amber-100 shrink-0">
                  <ExternalLink className="w-4 h-4 text-amber-600" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-slate-800">AVV Hetzner</h3>
                  <p className="text-xs text-slate-500 mt-0.5">Auftragsverarbeitungsvertrag mit Hetzner Online GmbH</p>
                  <span className="inline-block text-[10px] mt-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-600 font-mono">Art. 28 DSGVO</span>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="w-full gap-2 mt-auto border-amber-300 text-amber-700 hover:bg-amber-50"
                onClick={() => window.open('https://www.hetzner.com/legal/dpa', '_blank')}
                data-testid="btn-avv-hetzner"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Bei Hetzner abschließen
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
